import { defineStore } from 'pinia'
import { authService } from '@/services/authService'
import { userService } from '@/services/userService'

export const useUserStore = defineStore('user', {
  state: () => ({
    userId: null,
    userEmail: null,
    userName: null,
    userAvatar: null,
    userCash: 0,
    userExp: 0,
    trophies: [],
    // Cache for user items
    collection: {
      blades: null,
      rachets: null,
      bits: null
    },
    teams: null, // Cached user teams
    isLoaded: false,
    isLoggedIn: false,
  }),
  actions: {
    async fetchUser() {
      return new Promise((resolve) => {
        authService.onAuthStateChanged(async (user) => {
          if (user) {
            this.userId = user.uid
            this.userEmail = user.email
            this.isLoggedIn = true

            // 1. Get initial data
            let data = await userService.getUser(user.uid)

            // Setup realtime listener for cash & exp
            this._userUnsub?.() // cleanup previous if any
            this._userUnsub = userService.listenToUser(user.uid, (newData) => {
              if (!newData) return
              // Update cash if changed
              if (typeof newData.cash === 'number' && newData.cash !== this.userCash) {
                this.userCash = newData.cash
              }
              // Update exp & level if changed
              if (typeof newData.exp === 'number' && newData.exp !== this.userExp) {
                this.userExp = newData.exp
              }
            })

            if (data) {
              this.userName = data.username
              this.userAvatar = data.avatar
              this.userCash  = data.cash ?? 0
              this.userExp   = data.exp  ?? 0
            } else {
              // Create default user
              const newUser = {
                email: user.email,
                username: 'BladerX',
                avatar: 'character-2.png',
                cash: 0,
                exp: 0,
              }
              await userService.createUser(user.uid, newUser)
              
              this.userName = newUser.username
              this.userAvatar = newUser.avatar
              this.userCash = newUser.cash
              this.userExp = newUser.exp
            }

            // Setup trophies
            try {
              this.trophies = await userService.getTrophies(user.uid)
            } catch (err) {
              console.error('Failed to fetch trophies:', err)
              this.trophies = []
            }

            this.isLoaded = true
            resolve()
          } else {
            if (this._userUnsub) {
              this._userUnsub()
              this._userUnsub = null
            }
            this.isLoggedIn = false
            this.isLoaded = true
            resolve()
          }
        })
      })
    },
    async updateUsername(name) {
      await userService.updateUser(this.userId, { username: name })
      this.userName = name
    },
    async updateAvatar(avatar) {
      await userService.updateUser(this.userId, { avatar })
      this.userAvatar = avatar
    },
    _computeLevel(exp) {
      return Math.floor(Math.sqrt(exp / 500)) + 1
    },
    
    // --- COLLECTION CACHING ---
    async fetchUserCollection(category, forceRefresh = false) {
       const user = this.userId
       if (!user) return []
       
       const CACHE_KEY = `bx_collection_${user}_${category}`
       const CACHE_TIME = `bx_time_${user}_${category}`
       const TTL = 24 * 60 * 60 * 1000 // 24 hours

       // 1. Try Memory Cache
       if (!forceRefresh && this.collection[category]) {
         return this.collection[category]
       }

       // 2. Try LocalStorage
       if (!forceRefresh) {
           const cachedData = localStorage.getItem(CACHE_KEY)
           const cachedTime = localStorage.getItem(CACHE_TIME)
           
           if (cachedData && cachedTime) {
               const age = Date.now() - parseInt(cachedTime)
               if (age < TTL) {
                   try {
                       const parsed = JSON.parse(cachedData)
                       this.collection[category] = parsed
                       return parsed
                   } catch (e) {
                       console.warn("Invalid cache data", e)
                   }
               }
           }
       }
       
       // 3. Fetch from API
       try {
           const items = await userService.getUserCollection(this.userId, category)
           
           // Update Memory
           this.collection[category] = items
           
           // Update LocalStorage
           localStorage.setItem(CACHE_KEY, JSON.stringify(items))
           localStorage.setItem(CACHE_TIME, Date.now().toString())
           
           return items
       } catch (e) {
           console.error(`Error fetching ${category}`, e)
           return []
       }
    },
    
    clearCollectionCache(category) {
        if (!this.userId) return
        
        // Clear memory cache
        this.collection[category] = null
        
        // Clear localStorage
        const CACHE_KEY = `bx_collection_${this.userId}_${category}`
        const CACHE_TIME = `bx_time_${this.userId}_${category}`
        localStorage.removeItem(CACHE_KEY)
        localStorage.removeItem(CACHE_TIME)
    },
    
    // --- TEAMS CACHING ---
    async fetchUserTeams(forceRefresh = false) {
       const user = this.userId
       if (!user) return []
       
       const CACHE_KEY = `bx_teams_${user}`
       const CACHE_TIME = `bx_teams_time_${user}`
       const TTL = 24 * 60 * 60 * 1000 // 24 hours

       // 1. Try Memory Cache
       if (!forceRefresh && this.teams) {
         return this.teams
       }

       // 2. Try LocalStorage
       if (!forceRefresh) {
           const cachedData = localStorage.getItem(CACHE_KEY)
           const cachedTime = localStorage.getItem(CACHE_TIME)
           
           if (cachedData && cachedTime) {
               const age = Date.now() - parseInt(cachedTime)
               if (age < TTL) {
                   try {
                       const parsed = JSON.parse(cachedData)
                       this.teams = parsed
                       return parsed
                   } catch (e) {
                       console.warn("Invalid teams cache", e)
                   }
               }
           }
       }
       
       // 3. Fetch from API
       try {
           const teams = await userService.getUserTeams(this.userId)
           
           // Update Memory
           this.teams = teams
           
           // Update LocalStorage
           localStorage.setItem(CACHE_KEY, JSON.stringify(teams))
           localStorage.setItem(CACHE_TIME, Date.now().toString())
           
           return teams
       } catch (e) {
           console.error('Error fetching teams', e)
           return []
       }
    },
    
    clearTeamsCache() {
        if (!this.userId) return
        
        // Clear memory cache
        this.teams = null
        
        // Clear localStorage
        const CACHE_KEY = `bx_teams_${this.userId}`
        const CACHE_TIME = `bx_teams_time_${this.userId}`
        localStorage.removeItem(CACHE_KEY)
        localStorage.removeItem(CACHE_TIME)
    }
  }
})